import { UserManager, User, WebStorageStateStore } from "oidc-client-ts";
import { _t } from "../../languageHandler";

// Interfaccia della configurazione OIDC
export interface OidcConfig {
    authority: string;             // URL del provider OIDC (es. Azure AD)
    authorizationUrl?: string;      // Authorization Endpoint
    clientId: string;               // Client ID registrato sul provider OIDC
    tokenUrl?: string;              // Token Endpoint
    redirectUri: string;            // URL di callback dopo login
    postLogoutRedirectUri: string;  // URL di callback dopo logout
    scope: string;                  // Scope richiesti (es. "openid profile email")
    responseType?: string;          // Tipo di risposta (default: "code")
    provider: string;               // Provider OIDC             
}

/**
 * Classe singleton che gestisce il login OIDC con redirect.
 */
export class OidcAuthService {
    private static instance: OidcAuthService;
    private userManager: UserManager | null = null;
    private currentUser: User | null = null;
    private oidcConfig: OidcConfig | null = null;

    /**
     * Costruttore privato.
     * Recupera automaticamente la configurazione dal sessionStorage
     * alla creazione dell'istanza (es. dopo il redirect).
     */
    private constructor() {
        const config = sessionStorage.getItem("oidc_config");
        if (config) {
            this.setConfig(JSON.parse(config));
        }
    }

    /**
     * Restituisce l'unica istanza della classe (singleton).
     */
    public static getInstance(): OidcAuthService {
        if (!OidcAuthService.instance) {
            OidcAuthService.instance = new OidcAuthService();
        }
        return OidcAuthService.instance;
    }

    /**
     * Imposta o aggiorna la configurazione OIDC e crea un nuovo UserManager.
     * Salva anche la configurazione nel sessionStorage per poterla riutilizzare
     * dopo un redirect.
     */
    public setConfig(config: OidcConfig): void {
        // copia profonda della oidc config
        this.oidcConfig = JSON.parse(JSON.stringify(config));

        this.userManager = new UserManager({
            authority: config.authority,
            client_id: config.clientId,
            redirect_uri: config.redirectUri,
            response_type: config.responseType || "code",
            scope: config.scope,
            userStore: new WebStorageStateStore({ store: window.sessionStorage }),
            automaticSilentRenew: true,
            post_logout_redirect_uri: config.postLogoutRedirectUri || config.redirectUri,

            // Configurazione manuale dei metadata (senza discovery)
            metadata: {
                issuer: config.authority,
                authorization_endpoint: config.authorizationUrl,
                token_endpoint: config.tokenUrl,
            }
        });

        this.currentUser = null;

        // Persisti la configurazione per il post-redirect
        sessionStorage.setItem("oidc_config", JSON.stringify(config));
    }

    /**
     * Avvia il login tramite redirect.
     * L'utente verrà reindirizzato al provider OIDC.
     */
    public login(): Promise<void> {
        this.assertUserManager();
        return this.userManager!.signinRedirect();
    }

    /**
     * Gestisce la risposta dopo il redirect (callback).
     * Recupera i token e l'utente autenticato.
     */
    public async handleLoginCallback(): Promise<User | null> {
        this.assertUserManager();
        try {
            this.currentUser = await this.userManager!.signinRedirectCallback();
            return this.currentUser;
        } catch (error) {
            console.error("Errore durante la callback di login:", error);
            return null;
        }
    }

    /**
     * Restituisce l'utente corrente (se già autenticato).
     */
    public async getUser(): Promise<User | null> {
        this.assertUserManager();
        if (!this.currentUser) {
            this.currentUser = await this.userManager!.getUser();
        }
        return this.currentUser;
    }

    /**
     * Avvia il logout tramite redirect verso il provider OIDC.
     */
    public logout(): Promise<void> {
        this.assertUserManager();
        this.clearConfig();
        return this.userManager!.signoutRedirect();
    }

    /**
     * Gestisce la callback dopo il logout (opzionale).
     * Pulisce l'utente corrente.
     */
    public async handleLogoutCallback(): Promise<void> {
        this.assertUserManager();
        try {
            await this.userManager!.signoutRedirectCallback();
            this.currentUser = null;
        } catch (error) {
            console.error("Errore durante la callback di logout:", error);
        }
    }

    /**
     * Restituisce il client ID corrente.
     */
    public getOidcConfig(): OidcConfig | null {
        return this.oidcConfig;
    }

    /**
     * Rimuove la configurazione dal sessionStorage e resetta l'istanza.
     * Utile da chiamare dopo il logout.
     */
    public clearConfig(): void {
        sessionStorage.removeItem("oidc_config");
        this.userManager = null;
        this.currentUser = null;
    }

    /**
     * Metodo privato di sicurezza: verifica che userManager sia inizializzato.
     */
    private assertUserManager(): void {
        if (!this.userManager) {
            throw new Error(_t("error.user_manager_not_initialized_call_set_config_first"));
        }
    }
}
